import type {
	CloudEvent,
	EventBatchResult,
	EventBatchTransport,
	EventStore,
} from '@farmart-engineering/chronolog-sdk/events';

const IDENTIFIED = 'com.farmart.user.identified.v1';
const RETRY_DELAY_MS = 60_000;

/** Preserve rejected identity evidence even if logout happens before recovery. */
export function identityDelivery(
	storage: Storage,
	namespace: string,
	store: EventStore,
	transport: EventBatchTransport,
) {
	const prefix = `chronolog-rejected-identity:${namespace}:`;
	const lastAttempt = new Map<string, number>();
	let recovery: Promise<boolean> | undefined;
	function rejected(): CloudEvent[] {
		const events: CloudEvent[] = [];
		for (let index = 0; index < storage.length; index++) {
			const key = storage.key(index);
			if (!key?.startsWith(prefix)) continue;
			const raw = storage.getItem(key);
			if (!raw) continue;
			const event: CloudEvent = JSON.parse(raw);
			if (event.type !== IDENTIFIED || key !== prefix + event.id)
				throw new Error('Invalid stored identity link');
			events.push(event);
		}
		return events;
	}
	async function send(
		events: CloudEvent[],
	): Promise<undefined | EventBatchResult> {
		const result = await transport.send(events);
		if (
			result &&
			(result.outcomes.length !== events.length ||
				new Set(result.outcomes.map(outcome => outcome.id)).size !==
					events.length ||
				!result.outcomes.every(
					outcome =>
						events.some(event => event.id === outcome.id) &&
						['accepted', 'retryable', 'rejected'].includes(outcome.status) &&
						(outcome.reason === undefined ||
							(typeof outcome.reason === 'string' &&
								/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(outcome.reason))),
				))
		)
			return result;
		for (const event of events) {
			if (event.type !== IDENTIFIED) continue;
			const status = result
				? result.outcomes.find(outcome => outcome.id === event.id)?.status
				: 'accepted';
			if (status === 'rejected') {
				// Must persist before the SDK removes a terminally rejected record.
				storage.setItem(prefix + event.id, JSON.stringify(event));
				lastAttempt.set(event.id, Date.now());
			} else if (status === 'accepted') {
				storage.removeItem(prefix + event.id);
				lastAttempt.delete(event.id);
			}
		}
		return result || undefined;
	}
	return {
		transport: { send },
		store: {
			...store,
			load: () => {
				const events = new Map(store.load().map(event => [event.id, event]));
				for (const event of rejected()) {
					events.set(event.id, event);
					lastAttempt.set(event.id, Date.now());
				}
				return [...events.values()];
			},
		},
		// Explicit re-identification / reconnect can retry a rejected link, at
		// most once per minute. No periodic loop retries invalid schemas forever.
		recover: (): Promise<boolean> => {
			if (recovery) return recovery;
			recovery = (async () => {
				for (const event of rejected()) {
					if (Date.now() - (lastAttempt.get(event.id) ?? 0) < RETRY_DELAY_MS)
						continue;
					lastAttempt.set(event.id, Date.now());
					await send([event]);
				}
				return rejected().length === 0;
			})()
				.catch(() => false)
				.finally(() => {
					recovery = undefined;
				});
			return recovery;
		},
	};
}
